import { useState } from "react";
import { ArrowUp, RotateCcw, RotateCw, TriangleAlert } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { cn } from "@/lib/utils";
import { PORT_LETTERS, compassWord, drivetrainById, isFlipped, portConflicts, pushAngle, pushVector } from "@/rc/drivetrains";
import { RobotDiagram } from "./RobotDiagram";

const DirectionNote = ({ omni, m, dir }) => {
  if (omni) {
    const v = pushVector(m, dir);
    return <><ArrowUp className="h-4 w-4 text-[hsl(var(--rc-hot))]" style={{ transform: `rotate(${pushAngle(v)}deg)` }} />Wheel pushes {compassWord(v)}</>;
  }
  const Icon = dir === "R" ? RotateCcw : RotateCw;
  return <><Icon className="h-4 w-4 text-[hsl(var(--rc-hot))]" />Spins {dir === "R" ? "anticlockwise" : "clockwise"} to {m.steer ? "steer right" : "go forwards"}</>;
};

export const PortPicker = ({ m, value, conflict, onPick, prefix = "Port" }) => (
  <div className="mt-3 flex flex-wrap items-center gap-1.5 first:mt-0" role="radiogroup" aria-label={`${m.label} motor port`}>
    <span className="mr-1 min-w-9 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{prefix}</span>
    {PORT_LETTERS.map((p) => {
      const on = value === p;
      return (
        <button
          key={p}
          type="button"
          role="radio"
          aria-checked={on}
          onClick={() => onPick(p)}
          data-testid={`rc-port-${m.id}-${p}`}
          className={cn(
            "h-8 w-8 rounded-xl border font-display text-sm font-bold transition-[background-color,color,transform,border-color] hover:-translate-y-0.5 sm:h-9 sm:w-9",
            on ? (conflict ? "border-destructive bg-destructive text-destructive-foreground" : "border-primary bg-primary text-primary-foreground") : "bg-background hover:border-primary/50"
          )}
        >
          {p}
        </button>
      );
    })}
  </div>
);

const MotorRow = ({ omni, m, value, conflict, onChange, onActive }) => (
  <div onMouseEnter={onActive} onFocus={onActive} className={cn("rounded-2xl border bg-card p-4 transition-colors", conflict && "border-destructive/60")} data-testid={`rc-motor-row-${m.id}`}>
    <div className="flex flex-wrap items-center justify-between gap-2">
      <p className="font-display font-bold">{m.label} motor{m.steer ? " (steering)" : ""}</p>
      <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground" data-testid={`rc-direction-${m.id}`}>
        <DirectionNote omni={omni} m={m} dir={value.dir} />
      </span>
    </div>
    <PortPicker m={m} value={value.port} conflict={conflict} onPick={(port) => onChange({ port })} />
    <label className="mt-3 flex w-fit cursor-pointer items-center gap-2.5 text-sm font-medium">
      <Switch checked={isFlipped(m, value.dir)} onCheckedChange={() => onChange({ dir: value.dir === "F" ? "R" : "F" })} data-testid={`rc-reverse-${m.id}`} />
      Reverse direction
    </label>
  </div>
);

const SteerMax = ({ value, onChange }) => (
  <div className="rounded-2xl border bg-card p-4" data-testid="rc-steer-max">
    <div className="flex items-center justify-between">
      <p className="font-display font-bold">How far the front wheels turn</p>
      <span className="font-display font-bold text-primary" data-testid="rc-steer-max-value">{value}°</span>
    </div>
    <Slider className="mt-4" min={15} max={90} step={5} value={[value]} onValueChange={([v]) => onChange(v)} aria-label="Steering angle" data-testid="rc-steer-max-slider" />
    <p className="mt-3 text-xs text-muted-foreground">Point the front wheels straight ahead before you press Drive: that's where the hub counts as centre. Let go of the stick and they go back to straight.</p>
  </div>
);

export const PortsStep = ({ cfg, setCfg }) => {
  const dt = drivetrainById(cfg.drivetrain);
  const omni = dt.kind === "omni";
  const [active, setActive] = useState(dt.motors[0].id);
  const conflicts = portConflicts(cfg);
  const setMotor = (id, patch) => setCfg((c) => ({ ...c, motors: { ...c.motors, [id]: { ...c.motors[id], ...patch } } }));

  return (
    <div className="grid gap-6 lg:grid-cols-[300px_1fr]" data-testid="rc-ports-step">
      <div className="grid-bg self-start rounded-2xl border bg-muted/30 p-7 lg:sticky lg:top-0">
        <RobotDiagram dt={dt} cfg={cfg} mode="ports" active={active} conflicts={conflicts} className="mx-auto w-full max-w-[230px]" />
        <p className="mt-5 text-center text-xs font-semibold text-foreground">Seen from above, front at the top</p>
        <p className="mt-1 text-center text-xs text-muted-foreground">
          {omni ? "Orange arrows show which way each wheel pushes when its motor runs forwards." : "Orange arrows show which way each motor spins."}
        </p>
      </div>
      <div className="space-y-3">
        {dt.motors.map((m) => (
          <MotorRow key={m.id} omni={omni} m={m} value={cfg.motors[m.id]} conflict={conflicts.has(cfg.motors[m.id].port)} onChange={(patch) => setMotor(m.id, patch)} onActive={() => setActive(m.id)} />
        ))}
        {dt.kind === "car" && <SteerMax value={cfg.steerMax} onChange={(steerMax) => setCfg((c) => ({ ...c, steerMax }))} />}
        {conflicts.size > 0 ? (
          <p role="alert" className="flex items-center gap-2 rounded-xl bg-destructive/10 px-4 py-3 text-sm font-medium text-destructive" data-testid="rc-port-conflict">
            <TriangleAlert className="h-4 w-4 shrink-0" />Port {[...conflicts].join(", ")} is picked for more than one motor{cfg.attachments?.length ? " (attachments included)" : ""}. Each motor needs its own port.
          </p>
        ) : (
          <p className="px-1 text-xs text-muted-foreground">Not sure? Keep the defaults and test drive. If a wheel goes the wrong way, open Edit setup and switch on Reverse for that motor.</p>
        )}
      </div>
    </div>
  );
};
